import { Logtail } from '@logtail/browser'
import type { Context } from '@logtail/types'
import { LogLevel } from '@logtail/types'
import { isPresent } from '@mbfab/shared'
import { Logger } from 'tslog'

import { envVariables } from '../lib/env'

import type { LocalStorageUserInfo } from './getUserInfoFromLocalStorage'
import { getUserInfoFromLocalStorage } from './getUserInfoFromLocalStorage'
import type { LoggerEvent } from './logger_events'

class mbfabLogger {
  private readonly logger:
    | {
        type: 'logtail'
        logger: Logtail
      }
    | {
        type: 'tslog' // console
        logger: Logger<unknown>
      }
  private readonly tags: string[]

  public constructor(tags?: string[]) {
    this.tags = tags ?? []

    if (isPresent(envVariables.LOGTAIL_SOURCE_TOKEN)) {
      this.logger = {
        type: 'logtail',
        logger: new Logtail(envVariables.LOGTAIL_SOURCE_TOKEN),
      }
      this.logger.logger.use(async (log) => {
        return {
          ...log,
          NODE_ENV: envVariables.NODE_ENV ?? '',
        }
      })
      return
    }

    this.logger = {
      type: 'tslog',
      logger: new Logger({
        // logtailの設定が行われていない場合、代わりにtslogを使う
        overwrite: {
          mask: (args: unknown[]): unknown[] => {
            // logtailの第二引数に詳細パラメータを渡すことがあるが、開発中にこれが表示されると邪魔なので隠す？方針を悩み中。
            return [args[0]]
          },
        },
      }),
    }
    this.logger.logger.warn(`LOGTAIL_SOURCE_TOKEN is not specified`)
  }

  public async info(eventName: LoggerEvent, message: string, body?: Context) {
    const user = getUserInfoFromLocalStorage()
    if (this.logger.type === 'logtail') {
      await this.logger.logger.log(
        generateMessage(eventName, message, user),
        LogLevel.Info,
        { ...body, eventName, user },
        // info()ではなくlog()を使わないと以下のstackHintが使えない
        // 以下で起点となるファイル名を指定することで、このファイルの呼び元をログに残すことができる
        // -> というのをbackendのロジックで行っていたが、ブラウザ側ではファイル名の出力はできなそう？ TODO: 一旦本番運用しつつ検討予定
        // { fileName: 'logtail', methodNames: ['info'] },
      )
    } else {
      this.logger.logger.info(generateMessage(eventName, message, user), { ...body, eventName })
    }
  }

  public async warn(eventName: LoggerEvent, message: string, body?: Context) {
    const user = getUserInfoFromLocalStorage()
    if (this.logger.type === 'logtail') {
      await this.logger.logger.log(
        generateMessage(eventName, message, user),
        LogLevel.Warn,
        { ...body, eventName, user },
        // { fileName: 'logtail', methodNames: ['warn'] },
      )
    } else {
      this.logger.logger.warn(generateMessage(eventName, message, user), { ...body, eventName })
    }
  }

  public async error(eventName: LoggerEvent, message: string, body?: Context) {
    const user = getUserInfoFromLocalStorage()
    if (this.logger.type === 'logtail') {
      await this.logger.logger.log(
        generateMessage(eventName, message, user),
        LogLevel.Error,
        { ...body, eventName, user },
        // { fileName: 'logtail', methodNames: ['error'] },
      )
    } else {
      this.logger.logger.error(generateMessage(eventName, message, user), { ...body, eventName })
    }
  }

  // デバッグメッセージはeventTypeなしで使える形とする
  public async debug(message: string, body?: Context) {
    if (this.logger.type === 'logtail') {
      // debugログはlogtailに送信しない？
    } else {
      this.logger.logger.debug(message, body)
    }
  }

  public addTag(tag: string) {
    return new mbfabLogger([...this.tags, tag])
  }

  public async trackDuration<T>(eventName: LoggerEvent, logBody: Context, f: () => Promise<T>): Promise<T> {
    const start = new Date()
    try {
      const res = await f()
      const duration = new Date().getTime() - start.getTime()
      void this.info(
        eventName,
        [logBody.organizationName, logBody.viewName, logBody.userName, `${duration}ms`].join(' '),
        {
          ...logBody,
          duration,
          success: true,
        },
      )
      return res
    } catch (e) {
      const duration = new Date().getTime() - start.getTime()
      const errorMessage = e instanceof Error ? [e.name, e.message].join(': ') : `error`
      void this.info(
        eventName,
        [logBody.organizationName, logBody.viewName, logBody.userName, `${duration}ms`].join(' '),
        {
          ...logBody,
          duration,
          success: false,
          errorMessage,
        },
      )
      throw e
    }
  }
}

function generateMessage(eventName: LoggerEvent, message: string, user: LocalStorageUserInfo | undefined) {
  return `[${eventName}] ${user?.name ?? '(unknown user)'} ${user?.tenant.name} ${message}`
}

export const logger = new mbfabLogger()
